/** Phase 14 Wave 3 — Module D — Admin Leases page. */
import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminDataTable from "@/components/admin/AdminDataTable";
import { useFmt } from "@/lib/useFmt";
import { FileSignature, Loader2 } from "lucide-react";

const STATUS_BADGES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  ended: "bg-slate-100 text-slate-600 border-slate-200",
  terminated: "bg-red-50 text-red-700 border-red-200",
};

function TerminateDialog({ open, onOpenChange, lease, onDone }) {
  const [reason, setReason] = useState("");
  const [eff, setEff] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    setSaving(true);
    try {
      await api.post(`/admin/leases/${lease.id}/terminate`, { reason: reason.trim(), effective_date: eff });
      toast.success("Lease terminated");
      onDone?.();
      onOpenChange(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Termination failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lease-terminate">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="h-4 w-4 text-red-600" /> Terminate lease</DialogTitle>
          <DialogDescription>This will mark the lease as terminated and audit-log the action.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Effective date</Label>
            <Input type="date" value={eff} onChange={(e) => setEff(e.target.value)} data-testid="lease-terminate-date" className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              data-testid="lease-terminate-reason" className="mt-1 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={saving || !reason.trim()} data-testid="lease-terminate-submit">
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Terminate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminLeases() {
  const fmt = useFmt();
  const [refresh, setRefresh] = useState(0);
  const [terminating, setTerminating] = useState(null);
  const fetchPage = useCallback(async ({ skip, limit, q }) => {
    const page = Math.floor(skip / limit);
    const { data } = await api.get("/admin/leases", { params: { page, limit, q } });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);
  const columns = [
    { key: "id", label: "Lease", render: (r) => (
      <div>
        <div className="font-mono text-[10px] text-slate-400">{r.id?.slice(0, 8)}</div>
        <div className="text-xs text-slate-500">{r.tenant_id?.slice(0, 8) || "—"}</div>
      </div>
    )},
    { key: "monthly_rent", label: "Rent/mo", align: "right", render: (r) => fmt.money(r.monthly_rent) },
    { key: "security_deposit", label: "Deposit", align: "right", render: (r) => fmt.money(r.security_deposit) },
    { key: "start_date", label: "Start", render: (r) => r.start_date || "—" },
    { key: "end_date", label: "End", render: (r) => r.end_date || "—" },
    { key: "status", label: "Status", render: (r) => (
      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_BADGES[r.status] || STATUS_BADGES.ended}`}>{r.status}</span>
    )},
  ];
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Module D</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Leases</h1>
        <p className="mt-1 text-sm text-slate-500">All leases across all landlords + tenants.</p>
      </div>
      <AdminDataTable
        testid="admin-leases"
        columns={columns}
        fetchPage={fetchPage}
        searchPlaceholder="Search by tenant email or property address…"
        rowActions={(row) => row.status !== "terminated" ? [
          { label: "Terminate", variant: "danger", onClick: setTerminating },
        ] : []}
      />
      <TerminateDialog open={!!terminating} onOpenChange={(v) => !v && setTerminating(null)}
        lease={terminating} onDone={() => setRefresh((n) => n + 1)} />
    </div>
  );
}
